import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Sparkles, RefreshCw, MapPin, AlertTriangle } from "lucide-react";
import api, { apiError } from "../lib/api";

function Stat({ label, value, tone }) {
  return (
    <div className="text-center px-3 py-2" data-testid={`brief-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`text-2xl font-bold tabular-nums ${tone || "text-foreground"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

export default function DailyBrief() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);

  const load = () => api.get("/acq/brief").then((r) => setData(r.data)).finally(() => setLoading(false));
  useEffect(() => { document.title = "ARQITEK — Daily Acquisition Brief"; load(); }, []);

  const generate = async () => {
    setGen(true);
    try {
      const { data } = await api.post("/acq/brief/generate");
      setData(data);
      toast.success("Brief updated");
    } catch (e) { toast.error(apiError(e)); } finally { setGen(false); }
  };

  const brief = data?.brief;
  const stats = data?.stats || {};

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="daily-brief-page">
      <Link to="/ai-office" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="brief-back">
        <ArrowLeft className="h-4 w-4" /> AI Office
      </Link>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">Acquisition Intelligence</span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Daily Acquisition Brief</h1>
          <p className="text-sm text-muted-foreground mt-2">Angela's morning read on the pipeline{data?.date ? ` · ${data.date}` : ""}.</p>
        </div>
        <button onClick={generate} disabled={gen} className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium disabled:opacity-50" data-testid="brief-generate">
          {gen ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {brief ? "Regenerate" : "Generate today's brief"}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : !brief ? (
        <div className="rounded-xl border border-dashed border-border bg-card/40 p-12 text-center" data-testid="brief-empty">
          <Sparkles className="h-6 w-6 text-primary mx-auto mb-3" />
          <div className="font-semibold">No brief yet for today</div>
          <p className="text-sm text-muted-foreground mt-1">Generate Angela's brief to see today's priorities.</p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-primary/30 bg-primary/[0.04] p-5" data-testid="brief-headline">
            <div className="flex items-center gap-2 text-primary font-semibold"><Sparkles className="h-4 w-4" /> {brief.headline}</div>
            {brief.market_summary && <p className="text-sm text-foreground/90 mt-2">{brief.market_summary}</p>}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-y-2 rounded-xl border border-border bg-card py-3" data-testid="brief-stats">
            <Stat label="Total" value={stats.total ?? 0} />
            <div className="h-8 w-px bg-border" />
            <Stat label="Hot" value={stats.hot ?? 0} tone="text-emerald-600" />
            <div className="h-8 w-px bg-border" />
            <Stat label="Qualified" value={stats.qualified ?? 0} tone="text-blue-500" />
            <div className="h-8 w-px bg-border" />
            <Stat label="Nurture" value={stats.nurture ?? 0} tone="text-amber-600" />
            <div className="h-8 w-px bg-border" />
            <Stat label="New Today" value={stats.new_today ?? 0} />
            <div className="h-8 w-px bg-border" />
            <Stat label="Stale" value={stats.stale ?? 0} tone="text-orange-500" />
          </div>

          <div>
            <div className="text-sm font-semibold mb-2">Top Opportunities</div>
            {(brief.top_opportunities || []).length === 0 ? (
              <div className="text-sm text-muted-foreground">Nothing hot enough to act on today.</div>
            ) : (
              <div className="space-y-2" data-testid="brief-top-opps">
                {brief.top_opportunities.map((o, i) => (
                  <Link key={i} to={o.lead_id ? `/ai-office/leads/${o.lead_id}` : "#"} className="block rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary shrink-0" />
                      <span className="text-sm font-medium truncate">{o.address}</span>
                      <span className="ml-auto text-sm font-semibold tabular-nums">{o.score ?? "—"}</span>
                    </div>
                    {o.why && <div className="text-xs text-muted-foreground mt-1">{o.why}</div>}
                    {o.next_action && <div className="text-xs text-primary mt-1">Next: {o.next_action}</div>}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {(brief.needs_attention || []).length > 0 && (
            <div>
              <div className="text-sm font-semibold mb-2">Needs Attention</div>
              <div className="space-y-2" data-testid="brief-needs-attention">
                {brief.needs_attention.map((n, i) => (
                  <Link key={i} to={n.lead_id ? `/ai-office/leads/${n.lead_id}` : "#"} className="flex items-center gap-2 rounded-lg border border-orange-500/30 bg-orange-500/5 p-3 text-sm">
                    <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0" />
                    <span className="font-medium">{n.address}</span>
                    <span className="text-muted-foreground">— {n.reason}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {brief.angela_note && (
            <div className="rounded-xl border border-border bg-card p-4 text-sm" data-testid="brief-note">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Angela's note</span>
              <p className="mt-1">{brief.angela_note}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
